import { Flex, Box, Heading, Text, HStack } from '@chakra-ui/react';
import { createContext, useContext, type ReactNode } from 'react';

// Top-of-page header used by every page. Eyebrow / title / description
// on the left; primary + secondary actions on the right. Shape mirrors
// PageHeaderProps from the design spec.
//
// A consolidating surface (Settings v2) wraps its child pages in
// PageHeaderLevelContext so those pages demote to h2. The shell keeps
// the only h1. Default remains h1 for every standalone route.

export const PageHeaderLevelContext = createContext<'h1' | 'h2'>('h1');

type Props = {
  eyebrow?:         string;
  title:            string;
  description:      string;
  primaryAction?:   ReactNode;
  secondaryAction?: ReactNode;
  titleTestId?:     string;
};

export function PageHeader({ eyebrow, title, description, primaryAction, secondaryAction, titleTestId }: Props) {
  const level = useContext(PageHeaderLevelContext);
  return (
    <Flex
      align={{ base: 'flex-start', md: 'center' }}
      justify="space-between"
      gap={6}
      flexDirection={{ base: 'column', md: 'row' }}
    >
      <Box minW={0} flex={1}>
        {eyebrow && (
          <Text fontSize="xs" color="rsViolet.600" textTransform="uppercase" letterSpacing="0.08em" fontWeight="700" mb={2}>
            {eyebrow}
          </Text>
        )}
        <Heading as={level} size={level === 'h1' ? 'lg' : 'md'} color="brand.ink" mb={2} data-testid={titleTestId}>
          {title}
        </Heading>
        <Text color="brand.muted" maxW="640px">
          {description}
        </Text>
      </Box>
      {(primaryAction || secondaryAction) && (
        <HStack spacing={3} flexShrink={0}>
          {secondaryAction}
          {primaryAction}
        </HStack>
      )}
    </Flex>
  );
}
